import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X, Search } from "lucide-react";

/** A select you can type into. Options are plain strings; "" means no filter. */
export const SearchableSelect = ({
  options = [],
  value = "",
  onChange,
  placeholder = "Search...",
  allLabel = "All",
  emptyMessage = "Nothing matches",
  disabled = false,
  testId = "searchable-select",
}) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [active, setActive] = useState(0);
  const container = useRef(null);
  const input = useRef(null);

  const filtered = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.toLowerCase().includes(needle));
  }, [options, term]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event) => {
      if (container.current && !container.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setTerm("");
      setActive(0);
      input.current?.focus();
    }
  }, [open]);

  const choose = (next) => {
    onChange?.(next);
    setOpen(false);
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setActive((current) => {
        const last = filtered.length - 1;
        if (event.key === "ArrowDown") return current >= last ? 0 : current + 1;
        return current <= 0 ? last : current - 1;
      });
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (filtered[active] !== undefined) choose(filtered[active]);
    }
  };

  return (
    <div ref={container} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm flex items-center justify-between gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/50 transition-colors"
        data-testid={testId}
      >
        <span className={`truncate ${value ? "font-medium" : "text-muted-foreground"}`}>
          {value || allLabel}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {value && !disabled && (
            <X
              className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground"
              onClick={(event) => {
                event.stopPropagation();
                choose("");
              }}
              data-testid={`${testId}-clear`}
            />
          )}
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && !disabled && (
        <div
          className="absolute z-50 top-full left-0 right-0 mt-1 rounded-md border border-input bg-popover shadow-lg overflow-hidden"
          data-testid={`${testId}-menu`}
        >
          <div className="p-2 border-b border-input relative">
            <Search className="w-3.5 h-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={input}
              type="text"
              value={term}
              placeholder={placeholder}
              onChange={(event) => {
                setTerm(event.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              className="w-full h-8 pl-7 pr-2 rounded border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid={`${testId}-search`}
            />
          </div>

          <ul className="max-h-60 overflow-y-auto overscroll-contain py-1">
            <li>
              <button
                type="button"
                onClick={() => choose("")}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted ${
                  value ? "text-muted-foreground" : "font-medium"
                }`}
                data-testid={`${testId}-option-all`}
              >
                {allLabel} {options.length ? `(${options.length})` : ""}
              </button>
            </li>
            {filtered.length > 0 ? (
              filtered.map((option, index) => (
                <li key={option}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(index)}
                    onClick={() => choose(option)}
                    className={`w-full text-left px-3 py-2 text-sm truncate ${
                      index === active ? "bg-muted" : ""
                    } ${value === option ? "font-semibold text-primary" : ""}`}
                    data-testid={`${testId}-option-${option.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {option}
                  </button>
                </li>
              ))
            ) : (
              <li className="px-3 py-3 text-sm text-muted-foreground text-center" data-testid={`${testId}-empty`}>
                {emptyMessage}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
